<!-- CouponReservationCardModal.vue -->
<script setup>
import { ref, computed, watch } from "vue";
import { useCouponReservationStore } from "@/stores/useCouponReservationStore";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";
import api from "@/services/api";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  show:                 { type: Boolean, required: true },
  couponReservationId:  { type: Number,  required: true },
});

const emit = defineEmits(["close"]);

const store         = useCouponReservationStore();
const isLoadingData = ref(true);

// 🔹 NOVO: arrastar o modal pelo cabeçalho. Nem todo uso desmonta o componente
// entre aberturas, então o watch de `show` recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

watch(
  () => props.show,
  (val) => {
    if (val) resetPosition();
  }
);
const r             = ref(null);

// ─────────────────────────────────────────────
// FETCH — mesmo padrão de SubscriptionCardModal
// ─────────────────────────────────────────────
watch(
  () => props.couponReservationId,
  async (newId) => {
    if (!newId) return;
    isLoadingData.value = true;
    r.value = null;
    try {
      const cached = store.reservations.find(
        (x) => x.couponReservationId === newId,
      );
      if (cached) {
        r.value = cached;
      } else {
        const response = await api.get(`/CouponReservations/${newId}`);
        r.value = response.data;
      }
    } catch (e) {
      console.error("❌ Erro ao carregar coupon_reservation no CardModal:", e);
    } finally {
      isLoadingData.value = false;
    }
  },
  { immediate: true }
);

// ─────────────────────────────────────────────
// HELPERS DE STATUS
// ─────────────────────────────────────────────

// ✅ Chip baseado no reservationStatus computado no backend
//    (consumed > cancelled > expired > active)
const statusChip = computed(() => {
  if (!r.value) return { label: "—", cls: "badge-gray", icon: "fas fa-question-circle" };

  const s = String(r.value.reservationStatus ?? "").toLowerCase();
  const map = {
    active:    { label: "Ativa",     cls: "badge-green",  icon: "fas fa-check-circle" },
    consumed:  { label: "Consumida", cls: "badge-blue",   icon: "fas fa-coins" },
    cancelled: { label: "Cancelada", cls: "badge-red",    icon: "fas fa-ban" },
    expired:   { label: "Expirada",  cls: "badge-orange", icon: "fas fa-clock" },
  };
  return map[s] ?? { label: r.value.reservationStatus ?? "—", cls: "badge-gray", icon: "fas fa-question-circle" };
});

function displayVal(v) {
  return v != null && v !== "" ? v : "—";
}
</script>

<template>
  <div v-if="show" class="modal-overlay" @click.self="emit('close')">
    <div ref="panelRef" class="card-modal" :style="panelStyle">

      <!-- HEADER -->
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="card-title">
          Detalhes da Reserva de Cupom
          <span v-if="r" class="title-id">(ID: {{ r.couponReservationId }})</span>
        </h3>
        <button @click="emit('close')" class="modal-close-btn" aria-label="Fechar">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"/>
          </svg>
        </button>
      </div>

      <!-- LOADING -->
      <div v-if="isLoadingData" class="loading-state">
        <i class="fas fa-spinner fa-spin"></i> Carregando detalhes...
      </div>

      <!-- CONTEÚDO -->
      <div v-else-if="r" class="card-body">

        <!-- ── SEÇÃO: Identificação ───────────────────────────────── -->
        <section class="card-section">
          <h4 class="section-title">Identificação</h4>

          <div class="info-grid info-grid-3col" style="margin-bottom:0.5rem;">
            <div class="info-item">
              <span class="info-label">ID Reserva</span>
              <span class="info-value mono">{{ r.couponReservationId }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Cupom</span>
              <span class="info-value mono">
                {{ r.couponCode ?? r.couponId }}
                <span v-if="r.couponCode" class="id-muted">({{ r.couponId }})</span>
              </span>
            </div>
            <div class="info-item">
              <span class="info-label">Usuário</span>
              <span class="info-value mono">
                {{ r.userName ?? r.userId }}
                <span v-if="r.userName" class="id-muted">({{ r.userId }})</span>
              </span>
            </div>
          </div>

          <div class="info-grid info-grid-2col">
            <div class="info-item info-item-chip">
              <span class="info-label">Status</span>
              <span :class="['badge', statusChip.cls]">
                <i :class="statusChip.icon"></i> {{ statusChip.label }}
              </span>
            </div>
            <div class="info-item">
              <span class="info-label">E-mail do Usuário</span>
              <span class="info-value copyable-wrap">{{ displayVal(r.userEmail) }}</span>
            </div>
          </div>
        </section>

        <hr class="divider"/>

        <!-- ── SEÇÃO: Vínculo com Checkout ───────────────────────── -->
        <!-- ⚠️ SubscriptionId é preenchido pelo fluxo de checkout: destacado visualmente -->
        <section class="card-section">
          <h4 class="section-title">Vínculo com Assinatura</h4>

          <div class="info-grid info-grid-1col">
            <div class="info-item info-item-txn">
              <span class="info-label">
                ID Assinatura
                <span class="txn-hint">preenchido pelo checkout</span>
              </span>
              <span class="info-value mono">
                {{ r.subscriptionId != null ? r.subscriptionId : "— (reserva solta)" }}
              </span>
            </div>
          </div>
        </section>

        <hr class="divider"/>

        <!-- ── SEÇÃO: Datas ──────────────────────────────────────── -->
        <section class="card-section">
          <h4 class="section-title">Datas</h4>

          <!-- Linha 1: criação / expiração / atualização -->
          <div class="info-grid info-grid-3col" style="margin-bottom:0.5rem;">
            <div class="info-item">
              <span class="info-label">Criada em</span>
              <span class="info-value">{{ formatDateTimeForDisplay(r.createdAt) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Expira em</span>
              <span class="info-value">{{ formatDateTimeForDisplay(r.expiresAt) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Atualizada em</span>
              <span class="info-value">{{ formatDateTimeForDisplay(r.updatedAt) }}</span>
            </div>
          </div>

          <!-- Linha 2: campos transacionais (preenchidos pelo checkout) -->
          <div class="info-grid info-grid-2col">
            <div class="info-item info-item-txn">
              <span class="info-label">
                Consumida em
                <span class="txn-hint">preenchida pelo checkout</span>
              </span>
              <span class="info-value">
                {{ r.consumedAt ? formatDateTimeForDisplay(r.consumedAt) : "—" }}
              </span>
            </div>
            <div class="info-item info-item-txn">
              <span class="info-label">
                Cancelada em
                <span class="txn-hint">preenchida pelo checkout</span>
              </span>
              <span class="info-value">
                {{ r.cancelledAt ? formatDateTimeForDisplay(r.cancelledAt) : "—" }}
              </span>
            </div>
          </div>
        </section>

      </div>

      <!-- ERRO / vazio -->
      <div v-else class="loading-state">
        Reserva não encontrada.
      </div>

    </div>
  </div>
</template>

<style scoped>
.card-modal {
  background: var(--surface);
  border-radius: 12px;
  width: 90%;
  max-width: 680px;
  box-shadow: var(--shadow-modal);
  margin-bottom: 50px;
  overflow: hidden;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
}

.card-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
}

.title-id {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-left: 4px;
}

.card-body {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0;
  overflow-y: auto;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 9999px;
  white-space: nowrap;
}

.badge-green  { background-color: #b2f2bb; color: #2b8a3e; }
.badge-red    { background-color: #ffc9c9; color: #c92a2a; }
.badge-yellow { background-color: var(--warning-bg); color: #854d0e; }
.badge-orange { background-color: #ffe8cc; color: #9c4a00; }
.badge-blue   { background-color: #d0ebff; color: #1864ab; }
.badge-gray   { background-color: #f1f3f5; color: #495057; }

.card-section {
  padding: 0.25rem 0 0.75rem;
}

.section-title {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-subtle);
  margin: 0 0 0.6rem 0;
}

.divider {
  border: none;
  border-top: 1px dashed var(--border-color);
  margin: 0 0 0.75rem 0;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.5rem 1rem;
}

.info-grid-1col { grid-template-columns: 1fr !important; }
.info-grid-2col { grid-template-columns: repeat(2, 1fr) !important; }
.info-grid-3col { grid-template-columns: repeat(3, 1fr) !important; }

.info-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.info-item-chip {
  align-items: flex-start;
}

/* ✅ Destaque visual para campos transacionais (fluxo de checkout) */
.info-item-txn {
  background-color: var(--warning-bg);
  border-left: 3px solid #fbbf24;
  padding: 0.3rem 0.5rem;
  border-radius: 4px;
}

.info-label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.txn-hint {
  font-size: 0.62rem;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
  color: #b45309;
  background-color: var(--warning-bg);
  padding: 1px 6px;
  border-radius: 4px;
  font-style: italic;
}

.info-value {
  font-size: 0.88rem;
  color: var(--text-dark);
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.82rem;
}

.id-muted {
  color: var(--text-subtle);
  font-size: 0.78rem;
  margin-left: 2px;
}

.copyable-wrap {
  white-space: normal;
  overflow: visible;
  word-break: break-all;
}

</style>